import React, { useRef, useEffect } from 'react';

/**
 * Reusable MetaCircle Visualization Component
 * 
 * @param {Object} props
 * @param {Array} props.data - Array of data objects with { holdings, wallets, name, color? }
 * @param {number} props.totalSupply - Total supply for percentage calculations
 * @param {Object} props.config - Configuration options
 * @param {string} props.backgroundColor - Canvas background color
 * @param {number} props.width - Canvas width (optional, defaults to window width)
 * @param {number} props.height - Canvas height (optional, defaults to window height)
 * @param {Function} props.onCircleClick - Callback for circle clicks
 * @param {Function} props.onCircleHover - Callback for circle hover
 */
function MetaCircleVisualization({
  data = [],
  totalSupply,
  config = {},
  backgroundColor = '#0538AF',
  width,
  height,
  onCircleClick,
  onCircleHover
}) {
  const canvasRef = useRef(null);
  const initialized = useRef(false);
  const allCirclePairs = useRef([]);
  const connectionLayer = useRef();
  const circleLayer = useRef();

  // Merge default config with provided config
  const defaultConfig = {
    minRadius: 1.5,
    maxRadius: 560,
    handleLenRate: 2.4,
    scaleFactor: 100,
    margin: 100,
    maxConnectionDistance: 25,
    collisionColor: { r: 1, g: 0.8, b: 0.8, a: 1 },
    defaultColor: { r: 1, g: 1, b: 1, a: 1 },
    transparentOpacity: 0.2,
    transparentRadiusMultiplier: { min: 1.2, max: 6 },
    smallCategoryMultiplier: { min: 1.2, max: 2 },
    smallCategories: ['B', 'C', 'D', 'E', 'F']
  };
  
  const finalConfig = { ...defaultConfig, ...config };

  const random = (min, max) => Math.random() * (max - min) + min;

  const getRadius = (holdings) => {
    const percentage = holdings / totalSupply;
    return finalConfig.minRadius + (percentage * (finalConfig.maxRadius - finalConfig.minRadius));
  };

  const getVector = (radians, length) => {
    return new window.paper.Point({
      angle: radians * 180 / Math.PI,
      length: length
    });
  };

  const createCirclePair = (center, holdings, category, itemData) => {
    const solidRadius = getRadius(holdings);
    
    let transparentRadius;
    if (finalConfig.smallCategories.includes(category)) {
      transparentRadius = random(
        solidRadius * finalConfig.transparentRadiusMultiplier.min, 
        solidRadius * finalConfig.transparentRadiusMultiplier.max
      );
    } else {
      transparentRadius = random(
        solidRadius * finalConfig.smallCategoryMultiplier.min, 
        solidRadius * finalConfig.smallCategoryMultiplier.max
      );
    }
    
    // Use custom color if provided, otherwise default
    const color = itemData.color || finalConfig.defaultColor;
    
    const transparentCircle = new window.paper.Path.Circle({
      center: center,
      radius: transparentRadius,
      fillColor: new window.paper.Color(color.r, color.g, color.b, finalConfig.transparentOpacity)
    });
    
    const solidCircle = new window.paper.Path.Circle({
      center: center,
      radius: solidRadius,
      fillColor: new window.paper.Color(color.r, color.g, color.b, color.a),
      strokeColor: new window.paper.Color(color.r, color.g, color.b, color.a),
      strokeWidth: 1
    });
    
    const group = new window.paper.Group([transparentCircle, solidCircle]);
    
    // Add interaction handlers
    group.onMouseDrag = (event) => {
      group.translate(event.delta);
    };

    if (onCircleClick) {
      group.onClick = (event) => {
        onCircleClick(itemData, event);
      };
    }

    if (onCircleHover) {
      group.onMouseEnter = (event) => {
        onCircleHover(itemData, event, 'enter');
      };
      group.onMouseLeave = (event) => {
        onCircleHover(itemData, event, 'leave');
      };
    }
    
    const circlePair = {
      group,
      solidCircle,
      transparentCircle,
      transparentRadius,
      originalColors: {
        solid: solidCircle.fillColor.clone(),
        transparent: transparentCircle.fillColor.clone()
      },
      data: itemData
    };
    
    allCirclePairs.current.push(circlePair);
    return group;
  };

  const createBlobPath = (ball1, ball2, v, handle_len_rate, maxDistance) => {
    const center1 = ball1.position;
    const center2 = ball2.position;
    const radius1 = ball1.bounds.width / 2;
    const radius2 = ball2.bounds.width / 2;
    const pi2 = Math.PI / 2;
    const d = center1.getDistance(center2);
    let u1, u2;

    if (d > maxDistance || d <= Math.abs(radius1 - radius2)) {
      return undefined;
    }

    if (d < radius1 + radius2) {
      u1 = Math.acos((radius1 * radius1 + d * d - radius2 * radius2) / (2 * radius1 * d));
      u2 = Math.acos((radius2 * radius2 + d * d - radius1 * radius1) / (2 * radius2 * d));
    } else {
      u1 = 0;
      u2 = 0;
    }

    const angle1 = center2.subtract(center1).angleInRadians;
    const angle2 = Math.acos((radius1 - radius2) / d);
    const angle1a = angle1 + u1 + (angle2 - u1) * v;
    const angle1b = angle1 - u1 - (angle2 - u1) * v;
    const angle2a = angle1 + Math.PI - u2 - (Math.PI - u2 - angle2) * v;
    const angle2b = angle1 - Math.PI + u2 + (Math.PI - u2 - angle2) * v;

    const p1a = center1.add(getVector(angle1a, radius1));
    const p1b = center1.add(getVector(angle1b, radius1));
    const p2a = center2.add(getVector(angle2a, radius2));
    const p2b = center2.add(getVector(angle2b, radius2));

    const totalRadius = (radius1 + radius2);
    let d2 = Math.min(v * handle_len_rate, p1a.subtract(p2a).length / totalRadius);
    d2 *= Math.min(1, d * 2 / (radius1 + radius2));

    const r1 = radius1 * d2;
    const r2 = radius2 * d2;

    const path = new window.paper.Path({
      segments: [p1a, p2a, p2b, p1b],
      fillColor: new window.paper.Color('white'),
      closed: true
    });

    const segments = path.segments;
    segments[0].handleOut = getVector(angle1a - pi2, r1);
    segments[1].handleIn = getVector(angle2a + pi2, r2);
    segments[2].handleOut = getVector(angle2b - pi2, r2);
    segments[3].handleIn = getVector(angle1b + pi2, r1);

    return path;
  };

  const generateConnections = () => {
    if (!connectionLayer.current) return;
    connectionLayer.current.removeChildren();

    for (let i = 0; i < allCirclePairs.current.length; i++) {
      for (let j = i + 1; j < allCirclePairs.current.length; j++) {
        const pair1 = allCirclePairs.current[i];
        const pair2 = allCirclePairs.current[j];
        
        const distance = pair1.solidCircle.position.getDistance(pair2.solidCircle.position);
        const maxDistance = finalConfig.maxConnectionDistance + 
          (pair1.solidCircle.bounds.width + pair2.solidCircle.bounds.width) / 2;
        
        if (distance < maxDistance) {
          const connection = createBlobPath(
            pair1.solidCircle,
            pair2.solidCircle,
            0.5,
            finalConfig.handleLenRate,
            maxDistance
          );
          if (connection) {
            connectionLayer.current.addChild(connection);
          }
        }
      }
    }
  };

  const setupCanvas = () => {
    if (!canvasRef.current || !window.paper || initialized.current) return;

    window.paper.setup(canvasRef.current);
    connectionLayer.current = new window.paper.Layer();
    circleLayer.current = new window.paper.Layer();

    data.forEach((item) => {
      const numCircles = Math.max(1, Math.ceil(item.wallets / finalConfig.scaleFactor));

      for (let i = 0; i < numCircles; i++) {
        const canvasWidth = width || window.innerWidth;
        const canvasHeight = height || window.innerHeight;
        const x = random(finalConfig.margin, canvasWidth - finalConfig.margin);
        const y = random(finalConfig.margin, canvasHeight - finalConfig.margin);
        createCirclePair(new window.paper.Point(x, y), item.holdings, item.name, item);
      }
    });

    window.paper.view.onFrame = () => {
      allCirclePairs.current.forEach(pair1 => {
        pair1.solidCircle.fillColor = pair1.originalColors.solid;
        pair1.transparentCircle.fillColor = pair1.originalColors.transparent;

        allCirclePairs.current.forEach(pair2 => {
          if (pair1 !== pair2) {
            const distance = pair1.solidCircle.position.getDistance(pair2.solidCircle.position);
            const minDistance = (pair1.solidCircle.bounds.width + pair2.solidCircle.bounds.width) / 2;

            if (distance < minDistance) {
              const collisionColor = finalConfig.collisionColor;
              pair1.solidCircle.fillColor = new window.paper.Color(
                collisionColor.r, collisionColor.g, collisionColor.b, collisionColor.a
              );
              pair2.solidCircle.fillColor = new window.paper.Color(
                collisionColor.r, collisionColor.g, collisionColor.b, collisionColor.a
              );
              pair1.transparentCircle.fillColor = new window.paper.Color(
                collisionColor.r, collisionColor.g, collisionColor.b, 0.3
              );
              pair2.transparentCircle.fillColor = new window.paper.Color(
                collisionColor.r, collisionColor.g, collisionColor.b, 0.3
              );
            }
          }
        });
      });

      generateConnections();
    };

    initialized.current = true;
  };

  useEffect(() => {
    // Load paper.js if not already loaded
    if (!window.paper) {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/paper.js/0.12.17/paper-full.min.js';
      script.onload = () => {
        setupCanvas();
      };
      document.head.appendChild(script);
    } else {
      setupCanvas();
    }

    return () => {
      if (window.paper && window.paper.project) {
        window.paper.project.clear();
      }
      initialized.current = false;
      allCirclePairs.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, totalSupply, width, height]);

  return (
    <canvas 
      ref={canvasRef}
      style={{
        backgroundColor: backgroundColor,
        border: `1px solid ${backgroundColor}`,
        cursor: 'pointer'
      }}
      width={width || window.innerWidth} 
      height={height || window.innerHeight}
    />
  );
}

export default MetaCircleVisualization;